/**
 * Back in Stock
 *
 * Replaces Klaviyo's legacy onsite.js snippet. Unlike that one, the variant id is
 * passed explicitly from the trigger instead of being guessed from the DOM.
 *
 * Structure:
 *   - one modal per page (snippets/back-in-stock-modal.liquid, rendered in the layout)
 *   - any number of triggers ([data-bis-open]), each carrying its own variant id
 *   - clicks are delegated on document so lazy-loaded product tiles
 *     (theme.js, Section Rendering API) keep working without re-init
 */
(function () {
  "use strict";

  // Public API key — already exposed in the storefront today, safe to ship.
  var COMPANY_ID = "UAHrCy";
  var API_REVISION = "2026-07-15";

  // Newsletter list that back in stock signups are subscribed to. It is set to
  // double opt-in in Klaviyo, so the confirmation mail is sent by the list, not
  // triggered by this request.
  var LIST_ID = "Ue3ucz";

  var BIS_ENDPOINT =
    "https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=" + COMPANY_ID;
  var LIST_ENDPOINT =
    "https://a.klaviyo.com/client/subscriptions/?company_id=" + COMPANY_ID;

  // Deliberately permissive: the real check happens server-side at Klaviyo.
  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  var modal;
  var titleEl;
  var variantInput;
  var lastFocused = null;
  var isSubmitting = false;
  var submitLabel = "";

  var strings = (window.theme && theme.strings && theme.strings.bis) || {};

  /* ---------------------------------------------------------------- Modal --
   * Reimplementation of ccPopup (theme.js): that class lives inside an IIFE and
   * is not reachable from outside. We reuse its CSS classes so look and behaviour
   * stay identical.
   */

  function openModal(variantId, productTitle) {
    resetState();

    variantInput.value = variantId || "";
    titleEl.textContent = productTitle || "";

    lastFocused = document.activeElement;
    document.body.classList.add("cc-popup-no-scroll");
    modal.classList.add("cc-popup--visible");
    modal.setAttribute("aria-hidden", "false");
    document.addEventListener("keydown", onKeydown);

    // Focus only after the open transition, otherwise the page jumps
    setTimeout(function () {
      var email = modal.querySelector("#bis-modal-email");
      if (email) email.focus();
    }, 300);
  }

  function closeModal() {
    if (!modal.classList.contains("cc-popup--visible")) return;

    modal.classList.remove("cc-popup--visible");
    modal.setAttribute("aria-hidden", "true");
    document.body.classList.remove("cc-popup-no-scroll");
    document.removeEventListener("keydown", onKeydown);

    if (lastFocused && typeof lastFocused.focus === "function") {
      lastFocused.focus();
    }
    lastFocused = null;
  }

  function onKeydown(e) {
    if (e.key === "Escape" || e.key === "Esc") closeModal();
  }

  /* --------------------------------------------------------------- States -- */

  function resetState() {
    var form = modal.querySelector("[data-bis-form]");
    var success = modal.querySelector("[data-bis-success]");

    isSubmitting = false;
    setError(null);
    success.hidden = true;
    form.hidden = false;
    form.classList.remove("bis-modal__form--loading");
    form.reset();
    variantInput.value = "";

    var submit = modal.querySelector("[data-bis-submit]");
    submit.disabled = false;
    submit.textContent = submitLabel;
  }

  function setError(message) {
    var errorEl = modal.querySelector("[data-bis-error]");
    errorEl.textContent = message || "";
    errorEl.hidden = !message;
  }

  function setLoading(loading) {
    var form = modal.querySelector("[data-bis-form]");
    var submit = modal.querySelector("[data-bis-submit]");

    isSubmitting = loading;
    submit.disabled = loading;
    submit.textContent = loading ? strings.submitting || submitLabel : submitLabel;
    form.classList.toggle("bis-modal__form--loading", loading);
  }

  function showSuccess() {
    modal.querySelector("[data-bis-form]").hidden = true;
    modal.querySelector("[data-bis-success]").hidden = false;
  }

  /* ------------------------------------------------------------------ API --
   * Two independent calls go out per submission:
   *
   *   1. the back in stock subscription, which is what the customer asked for
   *   2. a list subscription, which puts the profile on the list and lets
   *      Klaviyo send the double opt-in mail
   *
   * They are unrelated on Klaviyo's side, so neither is a precondition for the
   * other and both run in parallel.
   */

  function postToKlaviyo(url, payload) {
    return fetch(url, {
      method: "POST",
      headers: {
        revision: API_REVISION,
        "Content-Type": "application/vnd.api+json"
      },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        if (response.ok) return { ok: true, status: response.status };

        return response
          .json()
          .catch(function () {
            return null;
          })
          .then(function (body) {
            return {
              ok: false,
              status: response.status,
              errors: (body && body.errors) || []
            };
          });
      })
      .catch(function (error) {
        // Network failure, offline, or a tracking blocker cutting off
        // a.klaviyo.com. Resolve instead of rejecting so one dead call cannot
        // take the other down with it.
        return { ok: false, status: 0, errors: [], networkError: true, error: error };
      });
  }

  /* The catalog variant id follows integrationType:::catalogId:::externalId, so
     a Shopify variant becomes $shopify:::$default:::<variant id>.
     Success is HTTP 202 with an empty body. */
  function backInStockPayload(email, variantId) {
    return {
      data: {
        type: "back-in-stock-subscription",
        attributes: {
          channels: ["EMAIL"],
          profile: { data: { type: "profile", attributes: { email: email } } }
        },
        relationships: {
          variant: {
            data: {
              type: "catalog-variant",
              id: "$shopify:::$default:::" + variantId
            }
          }
        }
      }
    };
  }

  function listSubscriptionPayload(email) {
    return {
      data: {
        type: "subscription",
        attributes: {
          profile: { data: { type: "profile", attributes: { email: email } } },
          custom_source: "Back in Stock"
        },
        relationships: {
          list: { data: { type: "list", id: LIST_ID } }
        }
      }
    };
  }

  function subscribe(email, variantId) {
    return Promise.all([
      postToKlaviyo(BIS_ENDPOINT, backInStockPayload(email, variantId)),
      postToKlaviyo(LIST_ENDPOINT, listSubscriptionPayload(email))
    ]).then(function (results) {
      return { backInStock: results[0], list: results[1] };
    });
  }

  /* Klaviyo's own error details are English and technical, so they only go to the
     console. The customer gets one of our own translated messages. */
  function messageFor(result) {
    var first = result.errors[0] || {};
    var pointer = (first.source && first.source.pointer) || "";

    if (pointer.indexOf("email") > -1 || pointer.indexOf("profile") > -1) {
      return strings.errorEmail;
    }
    if (pointer.indexOf("variant") > -1 || result.status === 404) {
      return strings.errorVariant;
    }
    return strings.errorGeneric;
  }

  function onSubmit(e) {
    e.preventDefault();
    if (isSubmitting) return;

    var emailInput = modal.querySelector("#bis-modal-email");
    var email = emailInput.value.trim();

    if (!EMAIL_PATTERN.test(email)) {
      setError(strings.errorEmail);
      emailInput.focus();
      return;
    }

    var variantId = variantInput.value;
    if (!variantId) {
      setError(strings.errorVariant);
      return;
    }

    setError(null);
    setLoading(true);

    subscribe(email, variantId)
      .then(function (result) {
        // The list subscription is secondary. If it fails the customer still gets
        // the restock notification they asked for, so it is logged but never
        // turned into an error message.
        if (!result.list.ok) {
          console.warn(
            "[back-in-stock] list subscription failed",
            result.list.status,
            result.list.errors
          );
        }

        // Klaviyo rejects duplicates; being subscribed already is a success to the
        // customer, so we do not surface it as an error.
        if (result.backInStock.ok || result.backInStock.status === 409) {
          showSuccess();
          return;
        }

        console.warn(
          "[back-in-stock] subscription failed",
          result.backInStock.status,
          result.backInStock.errors
        );
        setError(messageFor(result.backInStock));
      })
      .catch(function (error) {
        // Safety net: postToKlaviyo already resolves on network errors.
        console.warn("[back-in-stock]", error);
        setError(strings.errorGeneric);
      })
      .then(function () {
        setLoading(false);
      });
  }

  /* --------------------------------------------------------- Variant change --
   * theme.js fires 'cc-variant-updated' through $(window).trigger(). jQuery does
   * not dispatch a native DOM event for that, so addEventListener would never
   * fire — the listener has to go through jQuery.
   *
   * On section load it only broadcasts for products with more than one variant
   * (theme.js). Single-variant products are already rendered correctly server-side
   * and need no event.
   */

  /* theme.js only disables the add to cart button and relabels it to "sold out"
   * on variant change; server-rendered sold out products never get the button at
   * all. Hide it whenever we offer the notify option instead, so the two states
   * look the same and the customer is left with one clear action.
   *
   * The buy form is a sibling of the trigger wrapper in both block layouts.
   */
  function toggleBuyButton(wrapper, available) {
    // A product page carries several [data-add-to-cart] buttons: the buy block,
    // the sticky bar and the installments forms. Searching down from a shared
    // ancestor would return whichever comes first in the document, so walk back
    // to the form that directly precedes this wrapper instead — the trigger is
    // rendered right after {% endform %} in both blocks. Ids cannot be used
    // because both buy forms share product_form_id.
    var node = wrapper.previousElementSibling;

    while (node && node.tagName !== "FORM") {
      node = node.previousElementSibling;
    }
    if (!node) return;

    var button = node.querySelector("[data-add-to-cart]");
    if (!button) return;

    button.classList.toggle("bis-buy-hidden", !available);
  }

  function onVariantUpdated(e, args) {
    if (!args || !args.product) return;

    var wrappers = document.querySelectorAll(
      '[data-bis-trigger-wrapper][data-bis-product-id="' + args.product.id + '"]'
    );
    if (!wrappers.length) return;

    var variant = args.variant;
    var available = !!(variant && variant.available);

    Array.prototype.forEach.call(wrappers, function (wrapper) {
      wrapper.hidden = available;
      toggleBuyButton(wrapper, available);

      if (variant) {
        var trigger = wrapper.querySelector("[data-bis-open]");
        if (trigger) trigger.setAttribute("data-bis-variant-id", variant.id);
      }
    });

    // If someone switches variant while the modal is open, they would otherwise
    // subscribe to the previous variant.
    if (
      modal.classList.contains("cc-popup--visible") &&
      variant &&
      String(variant.id) !== String(variantInput.value)
    ) {
      closeModal();
    }
  }

  /* ----------------------------------------------------------------- Init -- */

  function init() {
    modal = document.querySelector("[data-bis-modal]");
    if (!modal) return;

    titleEl = modal.querySelector("[data-bis-title]");
    variantInput = modal.querySelector("[data-bis-variant-input]");
    submitLabel = modal.querySelector("[data-bis-submit]").textContent.trim();

    modal.querySelector("[data-bis-form]").addEventListener("submit", onSubmit);

    document.addEventListener("click", function (e) {
      var trigger = e.target.closest("[data-bis-open]");
      if (trigger) {
        e.preventDefault();
        openModal(
          trigger.getAttribute("data-bis-variant-id"),
          trigger.getAttribute("data-bis-product-title")
        );
        return;
      }

      if (e.target.closest("[data-bis-close]")) closeModal();
    });

    if (window.jQuery) {
      window.jQuery(window).on("cc-variant-updated.backInStock", onVariantUpdated);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
